import type { PendingSkillFileUpload } from '@heidi/agents';
import type { LinkedContextItem } from '@heidi/agents/contracts';
import { uploadValidationMessages, validateUploadFile } from '@heidi/evidence';
import { MAX_FILE_SIZE_MB } from '@heidi/evidence/contracts';
import { listUserDocuments, uploadUserDocument } from '@heidi/evidence/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { WorkflowArtifactRef } from '../types';
import { WORKFLOW_CONTEXT_DOCS_QUERY_KEY } from './LibraryDocumentPickerList';
import { waitForWorkflowContextDocument } from './waitForWorkflowContextDocument';

const workflowContextUploadMessages = defineMessages({
  tooLarge: {
    defaultMessage: 'File is too large (maximum {maxSizeMb, number} MB).',
    id: 'IHgXsKg4K3',
    description: 'Validation error when a routine context file exceeds the upload limit',
  },
  uploading: {
    defaultMessage: 'Uploading',
    id: 'MgSzIrgxsZ',
    description: 'Status while a routine context file upload starts',
  },
  uploadingWithProgress: {
    defaultMessage: 'Uploading {progress, number}%',
    id: 'KUz6QwuBPH',
    description: 'Status while a routine context file upload has measurable progress',
  },
  processing: {
    defaultMessage: 'Processing',
    id: 'BiL2HDn/J8',
    description: 'Status while a routine context file is processed',
  },
  processingLong: {
    defaultMessage: 'Processing. This can take a minute.',
    id: 's1/zw9zsmV',
    description: 'Status after a routine context file finishes uploading',
  },
  stillProcessing: {
    defaultMessage: 'Still processing. Pick the file from the list when it appears.',
    id: '0tyMCoJE2K',
    description: 'Status when routine context file processing exceeds the polling window',
  },
  processingFailed: {
    defaultMessage: 'Processing failed. Please try again.',
    id: '51tKRYeR1j',
    description: 'Error when routine context file processing fails',
  },
  uploadFailed: {
    defaultMessage: 'Upload failed. Please try again.',
    id: 'hrKqNCde1F',
    description: 'Error when a routine context file upload fails',
  },
});

const generateClientUploadId = () =>
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Shared upload pipeline for skill/context files (`document_kind: workflow_context`).
 * Used by the edit refs section and the create-flow section card.
 */
export function useWorkflowContextDocumentUpload(options: {
  onUploaded: (ref: WorkflowArtifactRef) => void;
  /** Called when the job finishes but the doc isn't listed yet — switch to pick UI. */
  onStillProcessing?: () => void;
}): {
  uploadPending: boolean;
  uploadStatus: string | null;
  uploadProgress: number | null;
  pendingUpload: PendingSkillFileUpload | null;
  setUploadStatus: (status: string | null) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  handleFileSelected: (fileList: FileList | null) => Promise<void>;
} {
  const { onUploaded, onStillProcessing } = options;
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [uploadPending, setUploadPending] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingSkillFileUpload | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      pollingAbortControllerRef.current?.abort();
    },
    []
  );

  const handleFileSelected = useCallback(
    async (fileList: FileList | null) => {
      const file = fileList?.[0];
      if (!file) return;

      const validation = validateUploadFile(file, intl, {
        unsupportedType: intl.formatMessage(uploadValidationMessages.supportedFormats),
        tooLarge: intl.formatMessage(workflowContextUploadMessages.tooLarge, {
          maxSizeMb: MAX_FILE_SIZE_MB,
        }),
      });
      if (!validation.valid) {
        setUploadStatus(validation.error);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setUploadPending(true);
      setUploadProgress(0);
      setUploadStatus(intl.formatMessage(workflowContextUploadMessages.uploading));
      setPendingUpload({
        jobId: '',
        filename: file.name,
        phase: 'uploading',
        uploadProgress: 0,
      });

      try {
        const clientUploadId = generateClientUploadId();
        const job = await uploadUserDocument(
          file,
          'user',
          (progress) => {
            setUploadProgress(progress);
            setUploadStatus(
              intl.formatMessage(workflowContextUploadMessages.uploadingWithProgress, {
                progress,
              })
            );
            setPendingUpload((current) =>
              current
                ? {
                    ...current,
                    uploadProgress: progress,
                  }
                : current
            );
          },
          null,
          clientUploadId,
          { document_kind: 'workflow_context' }
        );

        setUploadProgress(100);
        setUploadStatus(intl.formatMessage(workflowContextUploadMessages.processingLong));
        setPendingUpload({
          jobId: job.job_id,
          filename: file.name,
          phase: 'processing',
          uploadProgress: 100,
        });

        const abortController = new AbortController();
        pollingAbortControllerRef.current = abortController;
        const result = await waitForWorkflowContextDocument(job.job_id, file.name, {
          signal: abortController.signal,
          onProgress: () => {
            setUploadStatus(intl.formatMessage(workflowContextUploadMessages.processing));
            setPendingUpload((current) =>
              current
                ? {
                    ...current,
                    phase: 'processing',
                  }
                : current
            );
          },
        });

        void queryClient.invalidateQueries({
          queryKey: [WORKFLOW_CONTEXT_DOCS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({ queryKey: ['documents-and-jobs'] });
        void queryClient.invalidateQueries({
          queryKey: ['referenceable-library-documents', 'workflow_context'],
        });

        if (result.kind === 'ready') {
          onUploaded({
            type: 'document',
            id: result.document.id,
            label: result.document.name,
          });
          setUploadStatus(null);
          setUploadProgress(null);
          setPendingUpload(null);
        } else if (result.kind === 'timed_out') {
          onStillProcessing?.();
          setUploadStatus(
            intl.formatMessage(workflowContextUploadMessages.stillProcessing)
          );
          setPendingUpload(null);
        } else if (result.kind === 'failed') {
          setUploadStatus(
            intl.formatMessage(workflowContextUploadMessages.processingFailed)
          );
          setPendingUpload(null);
          setUploadProgress(null);
        }
      } catch {
        setUploadStatus(intl.formatMessage(workflowContextUploadMessages.uploadFailed));
        setPendingUpload(null);
        setUploadProgress(null);
      } finally {
        pollingAbortControllerRef.current = null;
        setUploadPending(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [intl, onStillProcessing, onUploaded, queryClient]
  );

  return {
    uploadPending,
    uploadStatus,
    uploadProgress,
    pendingUpload,
    setUploadStatus,
    fileInputRef,
    handleFileSelected,
  };
}

const DEFAULT_REFERENCEABLE_LIMIT = 8;

/** Work outputs + skill files the composer can @-mention or /-reference. */
export function useReferenceableLibraryDocuments({
  query,
  limit = DEFAULT_REFERENCEABLE_LIMIT,
}: {
  query?: string;
  limit?: number;
} = {}): LinkedContextItem[] {
  const { data: artefactData } = useQuery({
    queryKey: ['referenceable-library-documents', 'artifact'],
    queryFn: () => listUserDocuments('all', 'artifact'),
    staleTime: 30_000,
  });
  const { data: skillData } = useQuery({
    queryKey: ['referenceable-library-documents', 'workflow_context'],
    queryFn: () => listUserDocuments('all', 'workflow_context'),
    staleTime: 30_000,
  });

  return useMemo(() => {
    const needle = query?.trim().toLowerCase() ?? '';
    const matches = (filename: string): boolean =>
      needle === '' || filename.toLowerCase().includes(needle);

    const items: LinkedContextItem[] = [];

    for (const doc of artefactData?.documents ?? []) {
      if (items.length >= limit) break;
      if (!matches(doc.filename)) continue;
      items.push({
        type: 'library_document',
        documentId: doc.document_id,
        filename: doc.filename,
        documentKind: 'artifact',
      });
    }

    for (const doc of skillData?.documents ?? []) {
      if (items.length >= limit) break;
      if (!matches(doc.filename)) continue;
      items.push({
        type: 'library_document',
        documentId: doc.document_id,
        filename: doc.filename,
        documentKind: 'workflow_context',
      });
    }

    return items;
  }, [artefactData?.documents, limit, query, skillData?.documents]);
}
